import React, { useState, useRef, useEffect } from 'react'

const LazyImage = ({ src, alt, className = '', onLoad, onError, width = 160, height = 160, loading = 'lazy', ...props }) => {
  const [imageSrc, setImageSrc] = useState(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [placeholderSrc, setPlaceholderSrc] = useState(null)
  const imgRef = useRef(null)
  const observerRef = useRef(null)

  useEffect(() => {
    if (!src) return

    // Create a blurred placeholder from the actual image
    const createBlurredPlaceholder = () => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')

          // Set small dimensions for the placeholder
          canvas.width = 20
          canvas.height = 20

          // Draw and blur the image
          ctx.filter = 'blur(2px)'
          ctx.drawImage(img, 0, 0, 20, 20)

          // Convert to data URL
          const dataUrl = canvas.toDataURL('image/jpeg', 0.1)
          setPlaceholderSrc(dataUrl)
        } catch (error) {
          // If canvas fails, don't set any placeholder
          setPlaceholderSrc(null)
        }
      }
      img.onerror = () => {
        // If image fails to load, don't set any placeholder
        setPlaceholderSrc(null)
      }
      img.src = src
    }

    createBlurredPlaceholder()

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Image is in viewport, start loading
            setImageSrc(src)
            observer.unobserve(entry.target)
          }
        })
      },
      {
        rootMargin: '50px', // Start loading 50px before image comes into view
        threshold: 0.01,
      },
    )

    observerRef.current = observer

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [src])

  const handleLoad = () => {
    setImageLoaded(true)
    setImageError(false)
    onLoad?.()
  }

  const handleError = e => {
    setImageError(true)
    setImageLoaded(false)
    onError?.(e)
  }

  // Use placeholder until actual image is loaded, or show nothing if no placeholder
  const currentSrc = imageSrc || placeholderSrc

  return (
    <img
      ref={imgRef}
      src={currentSrc}
      alt={alt}
      className={`${className} ${imageLoaded ? 'loaded' : ''} ${imageError ? 'error' : ''}`}
      loading={loading}
      width={width}
      height={height}
      onLoad={handleLoad}
      onError={handleError}
      style={{
        opacity: imageLoaded ? 1 : 0.8,
        transition: 'opacity 0.3s ease-in-out',
        filter: imageLoaded ? 'none' : 'blur(1px)',
        transform: imageLoaded ? 'scale(1)' : 'scale(1.02)',
      }}
      {...props}
    />
  )
}

export default LazyImage
